import React from "react";

import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { FileUploadOverlay } from "@/ui/views/ViewFileUpload/FileUploadOverlay";
import { FileExtractionModalOverlay } from "@/ui/views/ViewFileExtractionModal/FileExtractionModalOverlay";
import {
    ViewAuthInit,
    ViewAuthGuard,
    ViewUserConsentModal,
    ViewAuthRoleGuard,
} from "@/ui/compositionRootUI";

import PageExtractionReview from "@/ui/pages/PageExtractionReview";
import PageExtractionMappings from "@/ui/pages/PageExtractionMappings";
import PageDealTable from "@/ui/pages/PageDealTable";
import PageDealCreation from "@/ui/pages/PageDealCreation";
import PageDealOverview from "@/ui/pages/PageDealOverview";
import PageDealDetail from "@/ui/pages/PageDealDetail";
import PageDealActivities from "@/ui/pages/PageDealActivities";
import PageAuthCallback from "@/ui/pages/PageAuthCallback";
import PageDashboard from "@/ui/pages/PageDashboard";
import PageThirdPartyDoc from "@/ui/pages/PageThirdPartyDoc";
import PageChartsOfAccountsDashboard from "@/ui/pages/PageChartsOfAccountsDashboard";
import PageChartsOfAccountsDetails from "@/ui/pages/PageChartsOfAccountsDetails";
import PageChartsOfAccountsMappings from "@/ui/pages/PageChartsOfAccountsMappings/PageChartsOfAccountsMappings";
import PageUserPolicies from "@/ui/pages/PageUserPolicies";
import PageChartsOfAccountsAuditLog from "@/ui/pages/PageChartsOfAccountsAuditLog";
import PageEditChartOfAccounts from "@/ui/pages/PageEditChartOfAccounts";
import PageCreateChartOfAccounts from "@/ui/pages/PageCreateChartOfAccounts";

import { CoAUploadOverlay } from "@/ui/views/ViewCoAUpload/CoAUploadOverlay";

import type {
    TDashboardTab,
    TDealsOverviewTab,
    TChartsOfAccountsTab,
    TChartOfAccountsEditTab,
} from "@/ui/router/routerPaths";
import type { TDealsDetailTab } from "@/ui/router/routerPaths";
import { routerPaths } from "@/ui/router/routerPaths";
import LegacyDealsRedirect from "@/ui/router/LegacyDealsRedirect";

import { NavigateExporter } from "./NavigateExporter";

const withConsentModal = (Component: React.FC) => {
    const WithConsentModal = () => {
        return (
            <>
                <Component />
                <ViewUserConsentModal />
            </>
        );
    };
    return WithConsentModal;
};

const AdminOnlyPageChartsOfAccountsDashboard = () => (
    <ViewAuthRoleGuard
        component={PageChartsOfAccountsDashboard}
        allowedRoles={["admin"]}
    />
);
const AdminOnlyPageChartsOfAccountsDetails = () => (
    <ViewAuthRoleGuard
        component={PageChartsOfAccountsDetails}
        allowedRoles={["admin"]}
    />
);
const AdminOnlyPageEditChartOfAccounts = () => (
    <ViewAuthRoleGuard
        component={PageEditChartOfAccounts}
        allowedRoles={["admin"]}
        prodaUserOnly={true}
    />
);

const AdminOnlyPageCreateChartOfAccounts = () => (
    <ViewAuthRoleGuard
        component={PageCreateChartOfAccounts}
        allowedRoles={["admin"]}
        prodaUserOnly={true}
    />
);

const AdminOnlyPageChartsOfAccountsMappings = () => (
    <ViewAuthRoleGuard
        component={PageChartsOfAccountsMappings}
        allowedRoles={["admin"]}
    />
);

const AdminOnlyPageChartsOfAccountsAuditLog = () => (
    <ViewAuthRoleGuard
        component={PageChartsOfAccountsAuditLog}
        allowedRoles={["admin"]}
    />
);

const ProtectedRoutes = () => {
    return (
        <ViewAuthInit>
            <Routes>
                <Route
                    path={routerPaths.main.default()}
                    element={
                        <ViewAuthGuard
                            component={withConsentModal(PageDealTable)}
                        />
                    }
                >
                    <Route
                        path={routerPaths.files.upload.path({
                            projectId: ":projectId",
                            databaseId: ":databaseId",
                        })}
                        element={
                            <ViewAuthGuard component={FileUploadOverlay} />
                        }
                    />
                </Route>
                <Route
                    path={routerPaths.dashboard.read(":tab" as TDashboardTab)}
                    element={<ViewAuthGuard component={PageDashboard} />}
                />
                <Route
                    path={routerPaths.projects.create({
                        databaseId: ":databaseId",
                    })}
                    element={
                        <ViewAuthGuard
                            component={withConsentModal(PageDealCreation)}
                        />
                    }
                />
                <Route
                    path={routerPaths.projects.read({
                        databaseId: ":databaseId",
                        projectId: ":projectId",
                        tab: ":tab" as TDealsOverviewTab,
                    })}
                    element={
                        <ViewAuthGuard
                            component={withConsentModal(PageDealOverview)}
                        />
                    }
                >
                    <Route
                        path={routerPaths.files.create({
                            projectId: ":projectId",
                            databaseId: ":databaseId",
                            fileId: ":fileId",
                        })}
                        element={
                            <ViewAuthGuard
                                component={FileExtractionModalOverlay}
                            />
                        }
                    />
                    <Route
                        path={routerPaths.files.upload.path({})}
                        element={
                            <ViewAuthGuard component={FileUploadOverlay} />
                        }
                    />
                </Route>
                <Route
                    path={routerPaths.projects.detail({
                        databaseId: ":databaseId",
                        projectId: ":projectId",
                        tab: ":tab" as TDealsDetailTab,
                    })}
                    element={
                        <ViewAuthGuard
                            component={withConsentModal(PageDealDetail)}
                        />
                    }
                />
                <Route
                    path={routerPaths.projects.activity({
                        databaseId: ":databaseId",
                        projectId: ":projectId",
                    })}
                    element={
                        <ViewAuthGuard
                            component={withConsentModal(PageDealActivities)}
                        />
                    }
                />
                <Route
                    path={routerPaths.extraction.mappings({
                        databaseId: ":databaseId",
                        projectId: ":projectId",
                        extractionId: ":extractionId",
                    })}
                    element={
                        <ViewAuthGuard
                            component={withConsentModal(PageExtractionMappings)}
                        />
                    }
                />
                <Route
                    path={routerPaths.extraction.review({
                        databaseId: ":databaseId",
                        projectId: ":projectId",
                        extractionId: ":extractionId",
                    })}
                    element={
                        <ViewAuthGuard
                            component={withConsentModal(PageExtractionReview)}
                        />
                    }
                />
                <Route
                    path="/deals/:dealId/*"
                    element={<ViewAuthGuard component={LegacyDealsRedirect} />}
                />
                <Route
                    path={routerPaths.auth.loginCallback()}
                    element={<PageAuthCallback />}
                />

                <Route
                    path={routerPaths.notFound.default()}
                    element={
                        <Navigate
                            to={routerPaths.main.default()}
                            replace={true}
                        />
                    }
                />

                <Route
                    path={routerPaths.userPolicies.default()}
                    element={<ViewAuthGuard component={PageUserPolicies} />}
                />

                <Route
                    path={routerPaths.chartsOfAccounts.dashboard(
                        ":tab" as TChartsOfAccountsTab,
                    )}
                    element={
                        <ViewAuthGuard
                            component={AdminOnlyPageChartsOfAccountsDashboard}
                        />
                    }
                ></Route>

                <Route
                    path={routerPaths.chartsOfAccounts.view(":coaId")}
                    element={
                        <ViewAuthGuard
                            component={AdminOnlyPageChartsOfAccountsDetails}
                        />
                    }
                />
                <Route
                    path={routerPaths.chartsOfAccounts.create()}
                    element={
                        <ViewAuthGuard
                            component={AdminOnlyPageCreateChartOfAccounts}
                        />
                    }
                ></Route>

                <Route
                    path={routerPaths.chartsOfAccounts.editCoA(
                        ":coaId",
                        ":tab" as TChartOfAccountsEditTab,
                    )}
                    element={
                        <ViewAuthGuard
                            component={AdminOnlyPageEditChartOfAccounts}
                        />
                    }
                >
                    <Route
                        path={routerPaths.chartsOfAccounts.upload(
                            ":coaId",
                            ":tab" as TChartOfAccountsEditTab,
                        )}
                        element={
                            <ViewAuthGuard
                                component={withConsentModal(CoAUploadOverlay)}
                            />
                        }
                    />
                </Route>

                <Route
                    path={routerPaths.chartsOfAccounts.editMappings(":coaId")}
                    element={
                        <ViewAuthGuard
                            component={AdminOnlyPageChartsOfAccountsMappings}
                        />
                    }
                />

                <Route
                    path={routerPaths.chartsOfAccounts.auditLog(":coaId")}
                    element={
                        <ViewAuthGuard
                            component={AdminOnlyPageChartsOfAccountsAuditLog}
                        />
                    }
                />
            </Routes>
        </ViewAuthInit>
    );
};

const Router = () => {
    return (
        <BrowserRouter>
            <NavigateExporter />
            <Routes>
                <Route path="/docs/" element={<PageThirdPartyDoc />} />
                <Route path="/*" element={<ProtectedRoutes />} />
            </Routes>
        </BrowserRouter>
    );
};

export default Router;
